import { Inject, Injectable, signal } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { Store } from '@ngrx/store';
import { WA_WINDOW } from '@ng-web-apis/common';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { FeedbackService } from 'src/app/shared/feedback.service';
import * as AppActions from 'src/app/+state/app/app.actions';

/**
 * Service to handle app version updates via Angular Service Worker.
 *
 * Monitors for new app versions and notifies users when updates are available.
 * Provides functionality to manually check for updates and reload the app.
 */
@Injectable({
  providedIn: 'root'
})
export class AppVersionService {
  readonly isUpdating = signal(false);
  private isStartup = true;

  constructor(
    @Inject(WA_WINDOW) private window: Window,
    private updates: SwUpdate,
    private store: Store,
    private feedbackService: FeedbackService,
    private translateService: TranslateService
  ) {
    console.log('AppVersionService initialized');

    // Subscribe to version update events
    this.updates.versionUpdates.subscribe((evt: VersionEvent) => {
      switch (evt.type) {
        case 'VERSION_DETECTED':
          console.log(`Downloading new app version: ${evt.version.hash}`);
          if (this.isStartup) {
            this.isUpdating.set(true);
          }
          break;

        case 'VERSION_READY':
          console.log(`Current app version: ${evt.currentVersion.hash}`);
          console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
          if (this.isStartup) {
            this.updates.activateUpdate().then(() => this.reloadApp());
          } else {
            this.store.dispatch(AppActions.newVersionAvailable({ available: true }));
            this.showUpdateNotification();
          }
          break;

        case 'VERSION_INSTALLATION_FAILED':
          console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
          if (this.isStartup) {
            this.isUpdating.set(false);
            this.isStartup = false;
          }
          break;
      }
    });

    // Trigger a check on startup; flip isStartup when resolved
    if (this.updates.isEnabled) {
      this.updates.checkForUpdate()
        .then(updateFound => { if (!updateFound) this.isStartup = false; })
        .catch(err => { console.error(err); this.isStartup = false; });
    }

    // Handle unrecoverable service worker errors
    this.updates.unrecoverable?.subscribe(event => {
      this.translateService.get([
        'APP.VERSION.ERROR',
        'APP.VERSION.PLEASE_RELOAD'
      ]).pipe(take(1)).subscribe(translations => {
        this.feedbackService.error(
          `${translations['APP.VERSION.ERROR']}: ${event.reason}\n\n${translations['APP.VERSION.PLEASE_RELOAD']}`
        );
      });
    });
  }

  /**
   * Show a notification to the user that a new version is available
   */
  private showUpdateNotification(): void {
    this.translateService.get([
      'APP.VERSION.NEW_AVAILABLE',
      'APP.VERSION.UPDATE_NOW'
    ]).pipe(take(1)).subscribe(translations => {
      const snackBarRef = this.feedbackService.snackbar.open(
        translations['APP.VERSION.NEW_AVAILABLE'],
        translations['APP.VERSION.UPDATE_NOW'],
        {
          duration: 0, // Don't auto-dismiss
          horizontalPosition: 'center',
          verticalPosition: 'bottom'
        }
      );

      // When user clicks "Update Now", reload the page
      snackBarRef.onAction().subscribe(() => {
        this.reloadApp();
      });
    });
  }

  /**
   * Manually check for updates (can be called from UI)
   */
  checkForUpdate(): void {
    if (!this.updates.isEnabled) {
      console.log('Service Worker is not enabled');
      return;
    }

    this.updates.checkForUpdate().then(updateFound => {
      if (updateFound) {
        console.log('Update check: New version found');
      } else {
        console.log('Update check: Already on latest version');
        this.translateService.get('APP.VERSION.ALREADY_LATEST')
          .pipe(take(1))
          .subscribe(msg => {
            this.feedbackService.success(msg);
          });
      }
    }).catch(err => {
      console.error('Error checking for updates:', err);
    });
  }

  /**
   * Reload the application to activate the new version
   */
  reloadApp(): void {
    // Clear the version available flag before reload
    this.store.dispatch(AppActions.newVersionAvailable({ available: false }));

    // Reload the page
    this.window.location.reload();
  }
}
